import { createSlice, nanoid, PayloadAction } from "@reduxjs/toolkit";
import { registerConfirm } from "../../services/confirmServices";
import type { RootState } from "../../app/store";

export type ModalType = "CONFIRM_GENERIC" | "ALERT_SUCCESS" | "ALERT_ERROR" | "ALERT_INFO";

export type BaseModal = {
  id: string;
  type: ModalType;
  title: string;
  subtitle?: string;
  confirmText?: string;
  cancelText?: string;
};

type ModalState = {
  stack: BaseModal[];
  lastAction?: "confirm" | "cancel";
};

const initialState: ModalState = { stack: [] };

const modalSlice = createSlice({
  name: "modal",
  initialState,
  reducers: {
    openModal: {
      reducer(state, action: PayloadAction<BaseModal>) {
        state.stack.push(action.payload);
      },
      prepare(payload: Omit<BaseModal, "id"> & { id?: string }) {
        return { payload: { id: payload.id ?? nanoid(), ...payload } as BaseModal };
      },
    },
    closeTopModal(state) {
      state.stack.pop();
    },
    confirmTopModal(state) {
      state.lastAction = "confirm";
      state.stack.pop();
    },
    cancelTopModal(state) {
      state.lastAction = "cancel";
      state.stack.pop();
    },

    // helpers
    openConfirm: {
      reducer(
        state,
        action: PayloadAction<Omit<BaseModal, "id" | "type"> & { id: string }>
      ) {
        state.stack.push({
          id: action.payload.id ?? nanoid(),
          type: "CONFIRM_GENERIC",
          title: action.payload.title,
          subtitle: action.payload.subtitle,
          confirmText: action.payload.confirmText ?? "Aceptar",
          cancelText: action.payload.cancelText ?? "Cancelar",
        });
      },
      prepare(
        payload: Omit<BaseModal, "id" | "type"> & {
          id?: string;
          onAccept?: () => void;
          onClose?: () => void;
        }
      ) {
        const id = payload.id ?? nanoid();
    
        // ⬅️ REGISTRAMOS LOS CALLBACKS AQUÍ
        if (payload.onAccept || payload.onClose) {
          registerConfirm(id, {
            onAccept: payload.onAccept,
            onClose: payload.onClose,
          });
        }
    
        // ⬅️ SOLO DEVOLVEMOS DATOS SERIALIZABLES
        return {
          payload: {
            id,
            title: payload.title,
            subtitle: payload.subtitle,
            confirmText: payload.confirmText,
            cancelText: payload.cancelText,
          },
        };
      },
    },
    openAlertSuccess(
      state,
      action: PayloadAction<
        Omit<BaseModal, "id" | "type" | "confirmText" | "cancelText"> & { id?: string }
      >
    ) {
      state.stack.push({
        id: action.payload.id ?? nanoid(),
        type: "ALERT_SUCCESS",
        title: action.payload.title,
        subtitle: action.payload.subtitle,
      });
    },
    openAlertError(
      state,
      action: PayloadAction<
        Omit<BaseModal, "id" | "type" | "confirmText" | "cancelText"> & { id?: string }
      >
    ) {
      state.stack.push({
        id: action.payload.id ?? nanoid(),
        type: "ALERT_ERROR",
        title: action.payload.title,
        subtitle: action.payload.subtitle,
      });
    },
    openAlertInfo(
      state,
      action: PayloadAction<
        Omit<BaseModal, "id" | "type" | "confirmText" | "cancelText"> & { id?: string }
      >
    ) {
      state.stack.push({
        id: action.payload.id ?? nanoid(),
        type: "ALERT_INFO",
        title: action.payload.title,
        subtitle: action.payload.subtitle,
      });
    },
  },
});

export const {
  openModal,
  closeTopModal,
  confirmTopModal,
  cancelTopModal,
  openConfirm,
  openAlertSuccess,
  openAlertError,
  openAlertInfo,
} = modalSlice.actions;

export const selectTopModal = (s: RootState) =>
  s.modal.stack.length ? s.modal.stack[s.modal.stack.length - 1] : null;

export default modalSlice.reducer;
